"use client"

import { useMemo, useState } from "react"
import { useParams } from "next/navigation"
import Image from "next/image"
import { Trash2, Loader2 } from "lucide-react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { getProductUrl } from "@lib/util/product"
import { getPricesForVariant } from "@lib/util/get-product-price"
import { convertToLocale } from "@lib/util/money"
import { addToCart } from "@lib/data/cart"

export type WishlistProductVariant = {
  id: string
  calculated_price?: {
    calculated_amount: number
    original_amount: number
    currency_code: string
  }
}

export type WishlistProduct = {
  id: string
  title: string
  handle: string
  thumbnail: string | null
  variants: WishlistProductVariant[]
}

type Props = {
  product: WishlistProduct
  onRemove: (productId: string) => void
}

export default function WishlistCard({ product, onRemove }: Props) {
  const countryCode = useParams().countryCode as string
  const [isAdding, setIsAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  const cheapestVariant = useMemo(() => {
    const variants = product.variants ?? []
    return [...variants]
      .filter((v) => !!v.calculated_price)
      .sort(
        (a, b) =>
          a.calculated_price!.calculated_amount -
          b.calculated_price!.calculated_amount
      )[0]
  }, [product.variants])

  const price = cheapestVariant ? getPricesForVariant(cheapestVariant) : null
  const hasDiscount =
    !!price && price.original_price_number > price.calculated_price_number

  const handleAddToCart = async () => {
    if (!cheapestVariant) return
    setIsAdding(true)
    setAddError(null)
    const result = await addToCart({
      variantId: cheapestVariant.id,
      quantity: 1,
      countryCode,
    })
    setIsAdding(false)
    if (!result.success) {
      setAddError(result.error || "Could not add to cart")
    }
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <LocalizedClientLink href={getProductUrl(product.handle)} className="block">
        <div className="relative aspect-[4/5] bg-gray-50">
          {product.thumbnail ? (
            <Image
              src={product.thumbnail}
              alt={product.title}
              fill
              className="object-contain p-3"
              sizes="(max-width: 768px) 50vw, 33vw"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-300">
              No image
            </div>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onRemove(product.id)
            }}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors z-10"
            aria-label={`Remove ${product.title} from wishlist`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </LocalizedClientLink>

      <div className="p-3">
        <LocalizedClientLink href={getProductUrl(product.handle)} className="block">
          <h3 className="text-sm font-semibold text-brand-primary line-clamp-2 mb-1.5 min-h-[40px]">
            {product.title}
          </h3>
        </LocalizedClientLink>

        {price && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-base font-bold text-gray-900">
              {price.calculated_price}
            </span>
            {hasDiscount && (
              <>
                <span className="text-sm text-gray-400 line-through">
                  {price.original_price}
                </span>
                <span className="text-xs font-semibold text-white bg-green-600 rounded-full px-2 py-0.5">
                  Save{" "}
                  {convertToLocale({
                    amount:
                      price.original_price_number - price.calculated_price_number,
                    currency_code: price.currency_code,
                  })}
                </span>
                <span className="text-xs font-semibold text-red-600">
                  {price.percentage_diff}% OFF
                </span>
              </>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={handleAddToCart}
          disabled={!cheapestVariant || isAdding}
          className="w-full py-2.5 text-sm font-semibold text-center rounded-full bg-brand-hero-navy text-white hover:bg-brand-primary-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isAdding && <Loader2 className="w-4 h-4 animate-spin" />}
          {isAdding ? "Adding..." : "Add to cart"}
        </button>
        {addError && <p className="text-xs text-red-600 mt-2">{addError}</p>}
      </div>
    </div>
  )
}
